import PageHeader from 'components/PageHeader/PageHeader';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import translations from 'translations';
import { Buyer, Category, NotificationMessage, Pagination, Store } from 'types/schema';

import media from 'utils/mediaStyle';
import messages from './messages';
import { List, Pagination as APagination, Spin } from 'antd';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { connect } from 'react-redux';
import { markNotificationAsRead, notifications, notificationsAsyncAction } from 'containers/MainLayout/actions';
import {
  selectBuyer,
  selectCategories,
  selectLang,
  selectNotificationData,
  selectStore,
} from 'containers/MainLayout/selectors';
import { selectIsTreatmentReady } from 'split/selectors';
import { NotificationsData } from 'containers/MainLayout/types';
import NotificationItem from 'components/NotificationItem';
import { parse, stringify } from 'utils/queryString';
import { RouteComponentProps, RouteProps, withRouter } from 'react-router-dom';

interface IStateProps {
  isTreatmentReady: boolean;
  notifications: NotificationsData;
  buyer: Buyer;
  store: Store;
  categories: Category[];
  lang?: string;
}

interface IDispatchProps {
  notificationsRequest: () => void
  getNotifications: (params: { pagination: Pagination; storeId: number }) => void;
  markNotificationAsRead: (messageId: string) => void;
}

type Props = IStateProps & IDispatchProps & RouteProps & RouteComponentProps;

const Container = styled.div`
  margin-top: 15px;
  background: #ffffff;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  @media (max-width: 1024px) {
    margin-top: 6px;
  }
`;

const PageContainer = styled(Container)`
  padding: 24px;
  ${media.md`
    padding: 24px;
  `};
`;

const PaginationContainer = styled.div`
  margin: 24px 0px;
  text-align: right;
`;

const NotificationPage: FC<Props> = (props) => {
  const { getNotifications, notificationsRequest, notifications, markNotificationAsRead, buyer, store, categories, lang, history, location } =
    props;

  const [currentPage, setCurrentPage] = useState<number>(1);

  const onChangePagination = useCallback((page: number) => {
    const queryString = stringify({
      page: page,
    });
    history.push({
      search: queryString,
    });
    getNotifications({ pagination: { page: page - 1, size: 20 }, storeId: store.internalId as number });
    window.scrollTo(0, 0);
    setCurrentPage(page);
  }, []);

  useEffect(() => {
    notificationsRequest()
    if (store.internalId) {
      const parseQueryString = parse(location.search);
      const page = parseQueryString?.pagination?.page ? Number(parseQueryString?.pagination?.page) - 1 : 0;
      setCurrentPage(page + 1);
      getNotifications({ pagination: { page: page, size: 20 }, storeId: store.internalId as number });
    }
  }, [store.internalId]);

  return (
    <PageContainer>
      <Helmet>
        <title>{translations(messages.notification)}</title>
        <meta name="description" content={`${translations(messages.notification)}`} />
      </Helmet>
      <PageHeader>{translations(messages.notification)}</PageHeader>
      <Container>
        <Spin spinning={notifications.loading}>
          <List
            dataSource={notifications?.data || []}
            renderItem={(item: NotificationMessage) => {
              return (
                <NotificationItem
                  notificationMessage={item}
                  buyerId={buyer?.id}
                  storeId={store?.id}
                  markNotificationAsRead={markNotificationAsRead}
                  categories={categories}
                  lang={lang || 'vi'}
                />
              );
            }}
          />
        </Spin>
      </Container>
      <PaginationContainer>
        <APagination
          onChange={onChangePagination}
          total={notifications.totalResults}
          current={currentPage}
          pageSize={20}
          showSizeChanger={false}
        />
      </PaginationContainer>
    </PageContainer>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  isTreatmentReady: selectIsTreatmentReady(),
  notifications: selectNotificationData(),
  buyer: selectBuyer(),
  store: selectStore(),
  categories: selectCategories(),
  lang: selectLang(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  notificationsRequest: () => dispatch(notificationsAsyncAction.request({})),
  getNotifications: (payload: { pagination: Pagination; storeId: number }) => dispatch(notifications(payload)),
  markNotificationAsRead: (messageId: string) => dispatch(markNotificationAsRead({ messageId: messageId })),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withRouter)(NotificationPage);
